import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CmsContentWorkflowStatus, CmsRenderArtifactStatus } from '@ndvr/cms-api';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MARKETING_CMS_SITES_PATH, marketingCmsSiteHistoryPath } from '@/views/marketing/marketing-routes';
import { useAuthContext } from 'views/auth/Auth';

import { useCmsPermissions } from './cms-permissions';
import {
    useApproveCmsVersion,
    useCmsHistory,
    useCmsMicrosite,
    useCmsVersionPreview,
    useRequestCmsChanges,
} from './cms-queries';
import { CmsSnapshotPreview } from './cms-rendering';
import { CmsArtifactSummary, CmsPageHeader, CmsSectionCard, CmsStatusBadge, latestWorkflowByVersion } from './cms-ui';

const CmsReviewPreviewPane = ({
    label,
    children,
}: React.PropsWithChildren<{
    label: string;
}>) => (
    <section className="min-w-0 space-y-3">
        <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold">{label}</h3>
        </div>
        {children}
    </section>
);

const CmsLiveSitePlaceholder = ({ title, message }: { title: string; message: string }) => (
    <div className="text-muted-foreground flex min-h-[520px] items-center justify-center rounded-md border border-dashed bg-white p-8 text-center text-sm">
        <div>
            <p className="font-medium text-stone-700">{title}</p>
            <p className="mt-1">{message}</p>
        </div>
    </div>
);

export const CmsReviewScreen = () => {
    const { cmsMicrositeGUID = '', cmsContentVersionGUID = '' } = useParams();
    const permissions = useCmsPermissions();
    const { user } = useAuthContext();
    const micrositeQuery = useCmsMicrosite(cmsMicrositeGUID);
    const historyQuery = useCmsHistory(cmsMicrositeGUID);
    const previewQuery = useCmsVersionPreview(cmsContentVersionGUID);
    const publishedPreviewQuery = useCmsVersionPreview(micrositeQuery.data?.publishedContentVersionGUID);
    const approveMutation = useApproveCmsVersion(cmsMicrositeGUID, cmsContentVersionGUID);
    const requestChangesMutation = useRequestCmsChanges(cmsMicrositeGUID, cmsContentVersionGUID);
    const [comment, setComment] = useState('');

    const version = historyQuery.data?.versions.find(
        (candidate) => candidate.cmsContentVersionGUID === cmsContentVersionGUID,
    );
    const reviewArtifact = useMemo(
        () =>
            previewQuery.data?.renderArtifact ??
            historyQuery.data?.renderArtifacts.find(
                (candidate) => candidate.cmsContentVersionGUID === cmsContentVersionGUID,
            ),
        [cmsContentVersionGUID, historyQuery.data?.renderArtifacts, previewQuery.data?.renderArtifact],
    );
    const latestStatus =
        version?.latestWorkflowStatus ?? latestWorkflowByVersion(historyQuery.data).get(cmsContentVersionGUID);
    const isReviewable = permissions.canReview && latestStatus === CmsContentWorkflowStatus.READY_FOR_REVIEW;
    const canRequestChanges = isReviewable;
    const isOwnSubmittedVersion = previewQuery.data?.snapshot.submittedBy === user?.personOrgGUID;
    const canApproveOwnSubmittedVersion = permissions.canSelfApprove;
    const canApprove =
        isReviewable &&
        (!isOwnSubmittedVersion || canApproveOwnSubmittedVersion) &&
        reviewArtifact?.status === CmsRenderArtifactStatus.SUCCEEDED &&
        !!reviewArtifact.metadata;

    const approve = () => {
        approveMutation.mutate({ comment });
    };

    const requestChanges = () => {
        if (comment.trim()) {
            requestChangesMutation.mutate({ comment });
        }
    };

    if (previewQuery.isError || micrositeQuery.isError || historyQuery.isError) {
        return (
            <>
                <CmsPageHeader
                    title="CMS review"
                    parent={{ label: 'Advisor Microsites', to: MARKETING_CMS_SITES_PATH }}
                />
                <div className="text-destructive p-6 text-sm">Unable to load this CMS review.</div>
            </>
        );
    }

    if (!previewQuery.data || !micrositeQuery.data) {
        return (
            <>
                <CmsPageHeader
                    title="CMS review"
                    parent={{ label: 'Advisor Microsites', to: MARKETING_CMS_SITES_PATH }}
                />
                <div className="text-muted-foreground p-6 text-sm">Loading review artifact...</div>
            </>
        );
    }

    if (!historyQuery.isLoading && !version) {
        return (
            <>
                <CmsPageHeader
                    title="CMS review"
                    parent={{ label: 'Advisor Microsites', to: MARKETING_CMS_SITES_PATH }}
                />
                <div className="text-destructive p-6 text-sm">This CMS version could not be found.</div>
            </>
        );
    }

    return (
        <>
            <CmsPageHeader
                title={`Review ${micrositeQuery.data.siteName}`}
                eyebrow={`/advisors/${micrositeQuery.data.slug}`}
                parent={{
                    label: 'Version history',
                    to: marketingCmsSiteHistoryPath(cmsMicrositeGUID),
                }}
                actions={<CmsStatusBadge status={latestStatus} />}
            />
            <div className="space-y-6 p-6">
                <div className="grid gap-5 xl:grid-cols-2">
                    <CmsReviewPreviewPane label="Version under review">
                        <CmsSnapshotPreview preview={previewQuery.data} />
                    </CmsReviewPreviewPane>
                    <CmsReviewPreviewPane label="Current live site">
                        {publishedPreviewQuery.data ? (
                            <CmsSnapshotPreview preview={publishedPreviewQuery.data} />
                        ) : micrositeQuery.data.publishedContentVersionGUID && publishedPreviewQuery.isLoading ? (
                            <CmsLiveSitePlaceholder
                                title="Loading current live site"
                                message="The published version preview is still loading."
                            />
                        ) : (
                            <CmsLiveSitePlaceholder
                                title="No live site yet"
                                message="This microsite does not have a published version to compare against."
                            />
                        )}
                    </CmsReviewPreviewPane>
                </div>
                <CmsSectionCard title="Review diagnostics">
                    <dl className="grid gap-3 text-sm md:grid-cols-2">
                        <div>
                            <dt className="text-muted-foreground">Version</dt>
                            <dd>{previewQuery.data.snapshot.versionNumber}</dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">Submitter</dt>
                            <dd className="break-all">{previewQuery.data.snapshot.submittedBy}</dd>
                        </div>
                    </dl>
                    <CmsArtifactSummary artifact={reviewArtifact} />
                </CmsSectionCard>
                <CmsSectionCard title="Compliance decision">
                    <Textarea
                        aria-label="Review comment"
                        placeholder="Comment for approval or requested changes"
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" disabled={!canApprove || approveMutation.isPending} onClick={approve}>
                            Approve version
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={!canRequestChanges || !comment.trim() || requestChangesMutation.isPending}
                            onClick={requestChanges}
                        >
                            Request changes
                        </Button>
                    </div>
                </CmsSectionCard>
            </div>
        </>
    );
};
